import React from 'react'
import { useIntl } from 'react-intl'
import { theme, Tooltip } from 'antd'
import { WarningOutlined } from '@ant-design/icons'
import { fontWeight, spacing } from '@vacationtracker/shared/constants/ThemeSetting'

import { AvatarStack, RecordCardLink, RecordCardMissing, RecordCardShell } from '../../design-system/RecordCard'
import { LoadingIndicator } from '../../design-system/LoadingIndicator'
import { StatusBadge } from '../../design-system/StatusBadge'
import { Text } from '../../design-system/Text'
import IntlMessages from '../../util/IntlMessages'

import { IPlace, missingKind, placeLabel, subdivisionTermKey } from '@vacationtracker/shared/data/countries'
import { getLocationFlags, ILocationFlag } from './flags'
import { ILocationUser } from '../../types/locations'

// One line: the name on the left, the details and the people on the right.
export const LOCATION_CARD_HEIGHT = 64
// Wide enough for three faces and "+99", so the details line up from card to card whatever the headcount.
const PEOPLE_WIDTH = 112
// The rule's width plus the gap after it.
const RULE_INSET = 1 + spacing.sm

interface ILocationCard {
  id: string
  title: string
  users: ILocationUser[]
  isDefault: boolean
  place: IPlace
  holidayCount: number
  leavePolicyCount: number
  disableEdit?: boolean
  /**
   * This location is still being filled in: its holidays and leave policies are seeded by events
   * that land after the one the create waited on, so a count of zero here means "not yet", not
   * "none". Shows a spinner in place of the missing marker until the counts arrive.
   */
  settling?: boolean
  /** Marks this card as the anchor for the admin product tour. One card per page. */
  quickTourLocation?: boolean
}

const LocationCard = ({ id, title, users, isDefault, place, holidayCount, leavePolicyCount, disableEdit, settling, quickTourLocation }: ILocationCard): React.ReactElement => {
  const { formatMessage } = useIntl()
  const { token } = theme.useToken()
  const flags = getLocationFlags({ name: title, isDefault })
  const missing = missingKind(place)

  const renderFlag = (flag: ILocationFlag) => (
    <StatusBadge key={flag.id} tone={flag.tone === 'warning' ? 'warning' : 'neutral'} icon={flag.tone === 'warning' ? <WarningOutlined /> : undefined}>
      {formatMessage({ id: flag.id })}
    </StatusBadge>
  )

  const placeFact =
    missing === 'country' ? (
      <RecordCardMissing size="base">
        <IntlMessages id="locationPlace.card.noCountrySet" />
      </RecordCardMissing>
    ) : (
      <Text style={{ color: token.colorTextSecondary }}>{placeLabel(place)}</Text>
    )

  // A country that expects a subdivision says so next to its name rather than replacing it.
  const subdivisionFact = missing === 'subdivision' && (
    <Text style={{ color: token.colorTextSecondary }}>
      <IntlMessages id="locationPlace.card.termNotSet" values={{ term: formatMessage({ id: subdivisionTermKey(place.countryCode) }) }} />
    </Text>
  )

  // The admin product tour anchors on these classes (context/onboardingContext/admin-steps.tsx).
  const countFact = (count: number, messageId: string, tourClass?: string) => (
    // Flex, like the place beside it: a warning left inline sits higher than the plain counts.
    <span className={quickTourLocation ? tourClass : undefined} style={{ display: 'flex' }}>
      {count === 0 && settling ? (
        <LoadingIndicator size="small" />
      ) : count === 0 ? (
        <RecordCardMissing size="base">
          <IntlMessages id={messageId} values={{ count }} />
        </RecordCardMissing>
      ) : (
        <Text style={{ color: token.colorTextSecondary }}>
          <IntlMessages id={messageId} values={{ count }} />
        </Text>
      )}
    </span>
  )

  const people =
    users.length === 0 ? (
      <RecordCardMissing size="base">
        <IntlMessages id="locationPlace.card.employees" values={{ count: 0 }} />
      </RecordCardMissing>
    ) : (
      <AvatarStack users={users} label={formatMessage({ id: 'locationPlace.card.employees' }, { count: users.length })} />
    )

  const separator = <span aria-hidden style={{ width: 1, height: 12, background: token.colorBorder }} />

  // Each detail holds together and carries the rule before it, so a wrap never splits a word.
  const detail = (node: React.ReactNode) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: spacing.sm, whiteSpace: 'nowrap' }}>
      {separator}
      {node}
    </span>
  )

  const name = (
    <Text
      size="lg"
      tone={disableEdit ? 'tertiary' : 'default'}
      style={{
        fontWeight: fontWeight.title,
        letterSpacing: '-0.3px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: 0,
      }}
    >
      {title}
    </Text>
  )

  const card = (
    <RecordCardShell height={LOCATION_CARD_HEIGHT} prominent>
      {/* Wraps when narrow: the details drop under the name, then under each other, rather than squeezing the name. */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: spacing.xl, rowGap: spacing.xs }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs, flex: '1 1 200px', minWidth: 0 }}>
          {disableEdit ? name : <RecordCardLink to={`/app/settings/locations/${id}/general`}>{name}</RecordCardLink>}
          {flags.map(renderFlag)}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: spacing.xl, rowGap: spacing.xs }}>
          {/* Every line starts with a rule; the negative margin pulls it outside the box and the clip hides it there, so
              a wrapped line never opens with one. Clipping only the left edge keeps focus rings intact.
              The clip, like the avatars' position, paints over the card link's overlay: pointer events pass through to it. */}
          <span
            style={{
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              columnGap: spacing.sm,
              rowGap: spacing.xxs,
              marginLeft: -RULE_INSET,
              clipPath: `inset(-8px -8px -8px ${RULE_INSET}px)`,
            }}
          >
            {detail(
              <span style={{ display: 'flex', gap: spacing.xs }}>
                {placeFact}
                {subdivisionFact}
              </span>
            )}
            {detail(countFact(holidayCount, 'locationPlace.card.holidays', 'joyride-location-holidays'))}
            {detail(countFact(leavePolicyCount, 'locationPlace.card.leaveTypes', 'joyride-location-leave-policies'))}
          </span>
          <span style={{ width: PEOPLE_WIDTH, display: 'flex', pointerEvents: 'none' }}>{people}</span>
        </div>
      </div>
    </RecordCardShell>
  )

  // A card that simply does not open reads as broken. The old list said why on its disabled Edit
  // action; this says the same thing on the card that replaced it.
  if (disableEdit) {
    return <Tooltip title={formatMessage({ id: 'locationPlace.card.notYours' })}>{card}</Tooltip>
  }

  return card
}

export default LocationCard
